import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { costingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Copy, Lock, Check, X, FileDown, Loader2, Trash2, Download, Printer, Eye, Info, Boxes,
} from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  pending_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700',
  locked: 'bg-purple-100 text-purple-700',
};

export function CostingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isManager, isAdmin } = useAuth();
  const [costing, setCosting] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCosting = useCallback(async () => {
    try {
      const response = await costingsAPI.get(id);
      setCosting(response.data.costing);
      setBreakdown(response.data.breakdown);
    } catch (error) {
      toast.error('Failed to fetch costing');
      navigate('/costings');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchCosting();
  }, [fetchCosting]);

  const handleApprove = async () => {
    try {
      await costingsAPI.approve(id, { comments: '' });
      toast.success('Costing approved');
      fetchCosting();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Approval failed');
    }
  };

  const handleReject = async () => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      await costingsAPI.reject(id, { reason });
      toast.success('Costing rejected');
      fetchCosting();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rejection failed');
    }
  };

  const handleLock = async () => {
    if (!window.confirm('Lock this costing? This action cannot be undone.')) return;

    try {
      await costingsAPI.lock(id);
      toast.success('Costing locked');
      fetchCosting();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Lock failed');
    }
  };

  const [showPrintPreview, setShowPrintPreview] = useState(false);

  const handlePreviewPdf = () => {
    setShowPrintPreview(true);
  };

  const [pdfDownloading, setPdfDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    try {
      setPdfDownloading(true);
      const { downloadPdfFromIframe } = await import('./print-views/shared');
      const filename = `${costing.costing_number || 'Costing'}_${costing.part_internal_no || ''}.pdf`;
      await downloadPdfFromIframe(
        '[data-testid="pdf-preview-iframe"]',
        'portrait',
        filename,
        null,
        (success) => {
          setPdfDownloading(false);
          if (success) toast.success('PDF downloaded');
          else toast.error('PDF generation failed');
        }
      );
    } catch {
      setPdfDownloading(false);
      toast.error('Failed to generate PDF. Please try Print instead.');
    }
  };

  const handleDelete = async () => {
    try {
      await costingsAPI.delete(id);
      toast.success('Costing deleted successfully');
      navigate('/costings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete costing');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!costing) return null;

  const previewUrl = `${window.location.origin}/costings/${id}/print?preview=true`;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* PDF Preview Modal */}
      <Dialog open={showPrintPreview} onOpenChange={setShowPrintPreview}>
        <DialogContent className="max-w-[900px] w-[95vw] h-[90vh] p-0 flex flex-col" data-testid="pdf-preview-modal">
          <DialogHeader className="px-6 pt-4 pb-2 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              Cost Calculation Sheet Preview
            </DialogTitle>
            <DialogDescription>
              {costing.costing_number} — {costing.part_internal_no}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-hidden">
            <iframe
              src={previewUrl}
              title="Costing Print Preview"
              className="w-full h-full border-0"
              data-testid="pdf-preview-iframe"
            />
          </div>
          <DialogFooter className="px-6 py-3 border-t shrink-0 flex flex-row items-center justify-end gap-2">
            <Button variant="outline" onClick={() => setShowPrintPreview(false)}>
              Close
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const iframe = document.querySelector('[data-testid="pdf-preview-iframe"]');
                if (iframe?.contentWindow) iframe.contentWindow.print();
              }}
            >
              <Printer className="mr-2 h-4 w-4" />
              Print
            </Button>
            <Button onClick={handleDownloadPdf} disabled={pdfDownloading} data-testid="download-pdf-btn">
              {pdfDownloading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {pdfDownloading ? 'Generating...' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">
              {costing.costing_number || `Costing V${costing.version_number}`}
            </h1>
            <Badge className={STATUS_COLORS[costing.status]}>
              {costing.status?.replace('_', ' ')}
            </Badge>
            {costing.is_locked && <Lock className="h-5 w-5 text-purple-500" />}
          </div>
          <p className="text-muted-foreground">
            {costing.part_internal_no} - {costing.part_description}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => navigate('/costings')}>Back</Button>
          <Button 
            variant="outline" 
            onClick={handlePreviewPdf}
            data-testid="preview-costing-pdf-btn"
          >
            <Eye className="mr-2 h-4 w-4" />
            View Cost Sheet
          </Button>
          <Button 
            variant="outline" 
            onClick={() => {
              // Open preview first, then user can click Download PDF
              handlePreviewPdf();
            }}
            data-testid="download-costing-pdf-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
          {isManager() && costing.status === 'draft' && (
            <>
              <Button variant="outline" onClick={handleReject}>
                <X className="mr-2 h-4 w-4" />
                Reject
              </Button>
              <Button className="btn-primary" onClick={handleApprove} data-testid="approve-costing-btn">
                <Check className="mr-2 h-4 w-4" />
                Approve
              </Button>
            </>
          )}
          {isManager() && costing.status === 'approved' && !costing.is_locked && (
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleLock}>
              <Lock className="mr-2 h-4 w-4" />
              Lock
            </Button>
          )}
          {costing.status === 'approved' && (
            <Button 
              className="btn-secondary"
              onClick={() => navigate(`/quotations/new`)}
            >
              Create Quotation
            </Button>
          )}
          {/* Delete Button with Confirmation - only show if not locked */}
          {!costing.is_locked && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  data-testid="delete-costing-btn"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Costing</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete costing <strong>{costing.costing_number}</strong>?
                    <br /><br />
                    This action cannot be undone. The costing record for <strong>{costing.part_internal_no}</strong> will be permanently removed.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDelete}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="confirm-delete-costing-btn"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Summary Card */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card className="card-metric">
          <p className="text-sm text-muted-foreground">Method</p>
          <p className="text-lg font-bold mt-1">
            {costing.costing_method === 'bom_rollup' ? 'BOM Rollup' : costing.costing_method === 'external_import' ? 'External Import' : 'Process Flow'}
          </p>
        </Card>
        <Card className="card-metric">
          <p className="text-sm text-muted-foreground">Cost/Piece</p>
          <p className="text-xl font-bold font-mono mt-1">₹{costing.total_cost_per_piece?.toFixed(4)}</p>
        </Card>
        <Card className="card-metric">
          <p className="text-sm text-muted-foreground">Sell/Piece</p>
          <p className="text-xl font-bold font-mono text-primary mt-1">₹{costing.selling_price_per_piece?.toFixed(4)}</p>
        </Card>
        <Card className="card-metric">
          <p className="text-sm text-muted-foreground">Cost/Kg</p>
          <p className="text-xl font-bold font-mono mt-1">₹{costing.total_cost_per_kg?.toFixed(4) || '-'}</p>
        </Card>
        <Card className="card-metric">
          <p className="text-sm text-muted-foreground">Cost/100 Pcs</p>
          <p className="text-xl font-bold font-mono mt-1">₹{costing.total_cost_per_100_pcs?.toFixed(4) || '-'}</p>
        </Card>
        <Card className="card-metric">
          <p className="text-sm text-muted-foreground">Cost/1000 Pcs</p>
          <p className="text-xl font-bold font-mono mt-1">₹{costing.total_cost_per_1000_pcs?.toFixed(4) || '-'}</p>
        </Card>
        <Card className="card-metric">
          <p className="text-sm text-muted-foreground">Margin</p>
          <p className="text-xl font-bold font-mono text-emerald-500 mt-1">{costing.margin_percentage}%</p>
        </Card>
      </div>

      {/* BOM Rollup Details (for Kits) */}
      {costing.costing_method === 'bom_rollup' && costing.bom_costs?.length > 0 && (
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg font-heading uppercase flex items-center gap-2">
              <Boxes className="h-5 w-5" />
              BOM Cost Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Child Part No</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Qty/Kit</TableHead>
                  <TableHead>Cost/Piece</TableHead>
                  <TableHead>Line Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costing.bom_costs.map((item, idx) => (
                  <TableRow key={item.child_part_no || idx} className="table-row">
                    <TableCell className="font-mono">{item.child_part_no}</TableCell>
                    <TableCell>{item.child_description}</TableCell>
                    <TableCell className="font-mono">{item.quantity_per_kit}</TableCell>
                    <TableCell className="font-mono">₹{item.child_cost_per_piece?.toFixed(4)}</TableCell>
                    <TableCell className="font-mono font-medium">₹{item.line_cost?.toFixed(4)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="mt-4 space-y-2 text-sm">
              {costing.assembly_labor_cost > 0 && (
                <div className="flex justify-between">
                  <span>Assembly Labor:</span>
                  <span className="font-mono">₹{costing.assembly_labor_cost.toFixed(4)}</span>
                </div>
              )}
              {costing.kit_packaging_cost > 0 && (
                <div className="flex justify-between">
                  <span>Kit Packaging:</span>
                  <span className="font-mono">₹{costing.kit_packaging_cost.toFixed(4)}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {costing.costing_method === 'external_import' && (
        <section className="border bg-white p-5" data-testid="external-costing-breakdown">
          <div className="mb-5 flex flex-col gap-2 border-b pb-4 sm:flex-row sm:items-end sm:justify-between"><div><h2 className="text-lg font-heading font-bold uppercase">Reviewed external costing</h2><p className="text-sm text-muted-foreground">Mapped from the retained source file; values were confirmed before activation.</p></div><Badge variant="outline" data-testid="external-costing-source-filename">{costing.source_filename}</Badge></div>
          <div className="overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Cost component</TableHead><TableHead>Category</TableHead><TableHead>Basis</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Amount / piece</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader><TableBody>{(costing.external_cost_components || []).map((component, index) => <TableRow key={component.id || index} data-testid={`external-cost-component-${index}`}><TableCell className="font-medium">{component.name}</TableCell><TableCell>{component.category?.replaceAll('_', ' ')}</TableCell><TableCell>{component.basis?.replaceAll('_', ' ')}</TableCell><TableCell className="text-right font-mono">{costing.external_currency} {Number(component.rate || 0).toFixed(4)}</TableCell><TableCell className="text-right font-mono">{costing.external_currency} {Number(component.amount_per_piece || 0).toFixed(4)}</TableCell><TableCell>{component.notes || '—'}</TableCell></TableRow>)}</TableBody></Table></div>
          {costing.external_notes && <p className="mt-4 border-l-2 border-blue-600 pl-3 text-sm" data-testid="external-costing-notes">{costing.external_notes}</p>}
        </section>
      )}

      {/* Detailed Breakdown (for Process Flow) */}
      {breakdown && costing.costing_method === 'process_flow' && (
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg font-heading uppercase flex items-center gap-2">
              <Info className="h-5 w-5" />
              Cost Breakdown with Formulas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" defaultValue={['raw-material', 'process', 'factors', 'totals']} className="space-y-2">
              {/* Raw Material */}
              <AccordionItem value="raw-material" className="border border-border rounded-lg px-4">
                <AccordionTrigger className="text-base font-semibold">
                  1. Raw Material Cost
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">RM Grade:</span>
                      <span className="font-semibold">{breakdown?.raw_material?.inputs?.rm_grade || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">RM Price:</span>
                      <span>{breakdown?.raw_material?.inputs?.rm_price_per_kg}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Scrap %:</span>
                      <span>{breakdown?.raw_material?.inputs?.scrap_percentage}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Net Weight:</span>
                      <span>{breakdown?.raw_material?.inputs?.net_weight}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-muted/30 rounded text-sm">
                    <p className="text-muted-foreground">Formula:</p>
                    <p className="font-mono text-xs mt-1">{breakdown?.raw_material?.formula}</p>
                  </div>
                  <div className="text-right font-bold text-primary">
                    Cost/Piece: {breakdown?.raw_material?.calculations?.cost_per_piece}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Process Costs */}
              <AccordionItem value="process" className="border border-border rounded-lg px-4">
                <AccordionTrigger className="text-base font-semibold">
                  2. Process Costs ({breakdown?.process_costs?.length || 0} steps)
                </AccordionTrigger>
                <AccordionContent className="space-y-4">
                  {breakdown?.process_costs?.map((pc, index) => (
                    <div key={pc.process_step_id || pc.process_name || index} className="space-y-2">
                      {/* Main Process Row */}
                      <div className={`p-3 rounded-lg ${pc.source === 'outsourced' ? 'bg-amber-50 border border-amber-200' : 'bg-muted/20'}`}>
                        <div className="flex justify-between items-center mb-2">
                          <h5 className="font-medium">{pc.process_name}</h5>
                          <div className="flex gap-2">
                            <Badge variant={pc.source === 'outsourced' ? 'default' : 'outline'} className={pc.source === 'outsourced' ? 'bg-amber-500' : ''}>
                              {pc.source === 'outsourced' ? 'OUTSOURCED' : 'IN-HOUSE'}
                            </Badge>
                            <Badge variant="outline">{pc.cost_basis}</Badge>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {pc.source === 'outsourced' ? (
                            <>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Vendor:</span>
                                <span className="font-semibold">{pc.inputs?.vendor_name || '-'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Vendor Rate:</span>
                                <span className="font-semibold">{pc.inputs?.vendor_rate || '-'}</span>
                              </div>
                            </>
                          ) : (
                            Object.entries(pc.inputs || {}).map(([key, value]) => (
                              <div key={key} className="flex justify-between">
                                <span className="text-muted-foreground">{key.replace(/_/g, ' ')}:</span>
                                <span>{value}</span>
                              </div>
                            ))
                          )}
                        </div>
                        {pc.formula && (
                          <div className="p-2 bg-white/50 rounded text-xs font-mono mt-2 text-muted-foreground">
                            {pc.formula}
                          </div>
                        )}
                        <div className="flex justify-between mt-2 font-medium border-t pt-2">
                          <span className="text-primary">Cost/Piece: {pc.cost_per_piece}</span>
                          <span className="text-muted-foreground">Cost/Kg: {pc.cost_per_kg}</span>
                        </div>
                      </div>
                      
                      {/* Setup Cost Sub-row (indented under parent process) */}
                      {pc.setup_cost && (
                        <div className="ml-6 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-amber-700 text-sm">↳</span>
                            <span className="font-medium text-amber-800 text-sm">Setup Cost</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs text-amber-700">
                            <span>Time: {pc.setup_cost.setup_time}</span>
                            <span>Rate: {pc.setup_cost.hourly_rate}/hr</span>
                            <span>Qty: {pc.setup_cost.quantity?.toLocaleString()} pcs</span>
                          </div>
                          <div className="flex justify-end mt-1 font-mono text-sm font-semibold text-amber-800">
                            {pc.setup_cost.cost_per_piece}
                          </div>
                        </div>
                      )}
                      
                      {/* Tooling Cost Sub-row (indented under parent process) */}
                      {pc.tooling_cost && (
                        <div className="ml-6 p-2 bg-purple-50 border border-purple-200 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-purple-700 text-sm">↳</span>
                            <span className="font-medium text-purple-800 text-sm">Tooling Cost</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs text-purple-700">
                            <span>Tool: {pc.tooling_cost.tool_cost}</span>
                            <span>Life: {pc.tooling_cost.tool_life}</span>
                          </div>
                          <div className="flex justify-end mt-1 font-mono text-sm font-semibold text-purple-800">
                            {pc.tooling_cost.cost_per_piece}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Unlinked Setup Costs (backward compatibility - show if not nested) */}
                  {breakdown?.setup_costs?.filter(sc => !breakdown.process_costs?.some(pc => pc.setup_cost && pc.process_step_id === sc.process_step_id)).length > 0 && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <h5 className="font-medium mb-2">Setup Costs (General)</h5>
                      {breakdown.setup_costs
                        .filter(sc => !breakdown.process_costs?.some(pc => pc.setup_cost && pc.process_step_id === sc.process_step_id))
                        .map((sc, idx) => (
                          <div key={sc.process_step_id || `setup-${idx}`} className="flex justify-between text-sm">
                            <span>{sc.process_name ? `${sc.process_name}: ` : ''}Setup ({sc.setup_time}) @ {sc.hourly_rate} / {sc.quantity} pcs</span>
                            <span className="font-mono">{sc.cost_per_piece}</span>
                          </div>
                        ))}
                    </div>
                  )}
                  
                  {/* Unlinked Tooling Costs (backward compatibility - show if not nested) */}
                  {breakdown?.tooling_costs?.filter(tc => !breakdown.process_costs?.some(pc => pc.tooling_cost && pc.process_step_id === tc.process_step_id)).length > 0 && (
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <h5 className="font-medium mb-2">Tooling Costs (General)</h5>
                      {breakdown.tooling_costs
                        .filter(tc => !breakdown.process_costs?.some(pc => pc.tooling_cost && pc.process_step_id === tc.process_step_id))
                        .map((tc, idx) => (
                          <div key={tc.process_step_id || `tool-${idx}`} className="flex justify-between text-sm">
                            <span>{tc.process_name}: {tc.tool_cost} / {tc.tool_life}</span>
                            <span className="font-mono">{tc.cost_per_piece}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>

              {/* Factor Costs (IRR, CRR, Quality, Overhead, Finance, Risk) */}
              <AccordionItem value="factors" className="border border-border rounded-lg px-4">
                <AccordionTrigger className="text-base font-semibold">
                  3. Factor Costs (Multipliers Applied)
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  {/* Rejection (IRR + CRR) */}
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="font-medium text-red-700">Rejection Cost (IRR + CRR)</h5>
                      <span className="font-mono font-bold text-red-700">{breakdown?.rejection?.result}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Internal Rejection (IRR):</span>
                        <span>{breakdown?.rejection?.inputs?.irr_percentage}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Customer Returns (CRR):</span>
                        <span>{breakdown?.rejection?.inputs?.crr_percentage}</span>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Formula: {breakdown?.rejection?.formula}
                    </div>
                  </div>

                  {/* Quality / Inspection */}
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="font-medium text-green-700">Quality / Inspection & Testing</h5>
                      <span className="font-mono font-bold text-green-700">{breakdown?.quality?.result}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Inspection %: </span>
                      <span>{breakdown?.quality?.inputs?.inspection_testing_percentage}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Formula: {breakdown?.quality?.formula}
                    </div>
                  </div>

                  {/* Packaging & Logistics */}
                  <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="font-medium text-cyan-700">Packaging & Logistics</h5>
                      <span className="font-mono font-bold text-cyan-700">{breakdown?.packaging_logistics?.total}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Packaging (₹/Kg):</span>
                        <span>{breakdown?.packaging_logistics?.inputs?.packaging_per_kg}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Logistics (₹/Kg):</span>
                        <span>{breakdown?.packaging_logistics?.inputs?.logistics_per_kg}</span>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Formula: {breakdown?.packaging_logistics?.formula}
                    </div>
                  </div>

                  {/* Overheads */}
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="font-medium text-orange-700">Factory Overheads</h5>
                      <span className="font-mono font-bold text-orange-700">{breakdown?.overhead?.result}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Overhead %: </span>
                      <span>{breakdown?.overhead?.inputs?.overhead_percentage}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Formula: {breakdown?.overhead?.formula}
                    </div>
                  </div>

                  {/* Finance & Risk */}
                  <div className="p-3 bg-violet-50 rounded-lg border border-violet-200">
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="font-medium text-violet-700">Finance & Risk Premium</h5>
                      <span className="font-mono font-bold text-violet-700">{breakdown?.finance_risk?.result}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Finance Cost %:</span>
                        <span>{breakdown?.finance_risk?.inputs?.finance_percentage}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Risk Premium %:</span>
                        <span>{breakdown?.finance_risk?.inputs?.risk_percentage}</span>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Formula: {breakdown?.finance_risk?.formula}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Final Totals */}
              <AccordionItem value="totals" className="border border-primary/50 rounded-lg px-4 bg-primary/5">
                <AccordionTrigger className="text-base font-semibold text-primary">
                  4. Final Totals & Selling Price
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <h5 className="font-medium text-muted-foreground border-b pb-1">Total Manufacturing Cost</h5>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span>Per Piece:</span>
                          <span className="font-mono font-bold">{breakdown?.totals?.cost_per_piece}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Per Kg:</span>
                          <span className="font-mono">{breakdown?.totals?.cost_per_kg}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Per 100 Pcs:</span>
                          <span className="font-mono">{breakdown?.totals?.cost_per_100_pcs}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Per 1000 Pcs:</span>
                          <span className="font-mono">{breakdown?.totals?.cost_per_1000_pcs}</span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <h5 className="font-medium text-primary border-b pb-1">Selling Price (@ {breakdown?.selling_price?.margin_percentage} margin)</h5>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span>Per Piece:</span>
                          <span className="font-mono text-primary font-bold text-lg">{breakdown?.selling_price?.per_piece}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Per Kg:</span>
                          <span className="font-mono text-primary font-bold">{breakdown?.selling_price?.per_kg}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Per 100 Pcs:</span>
                          <span className="font-mono text-primary font-bold">{breakdown?.selling_price?.per_100_pcs}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Per 1000 Pcs:</span>
                          <span className="font-mono text-primary font-bold">{breakdown?.selling_price?.per_1000_pcs}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-white rounded text-xs text-muted-foreground">
                    <strong>Selling Price Formula:</strong> {breakdown?.selling_price?.formula}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
